import React, { useEffect, useState } from "react";
import { http } from "@/lib/api";
import {
  Search, MapPin, Mail, MessageCircle, Send, Instagram, Globe,
  Filter, X, ExternalLink, Heart, GraduationCap, ChevronDown, ChevronUp,
  Map as MapIcon, List, Link2, Check,
} from "lucide-react";
import ReteMap from "@/components/ReteMap";
import { ReteFiltersPanel, ActiveFilters, EMPTY_FILTERS, countActive, toParams, fromParams } from "@/components/ReteFilters";
import { useSearchParams, Link } from "react-router-dom";

// Dot indicator for formation level
const LEVEL_META = {
  "sì":              { label: "Formazione specifica",     dots: 3, color: "bg-fucsia" },
  "più sì che no":   { label: "Formazione significativa", dots: 2, color: "bg-fucsia/70" },
  "più no che sì":   { label: "Formazione parziale",      dots: 1, color: "bg-fucsia/40" },
};

export function LevelDots({ level }) {
  const meta = LEVEL_META[level];
  if (!meta) return null;
  return (
    <span className="inline-flex gap-0.5 align-middle ml-1" title={meta.label} aria-label={meta.label}>
      {[0,1,2].map((i) => (
        <span key={`dot-${i}`} className={`inline-block w-1 h-1 rounded-full ${i < meta.dots ? meta.color : "bg-viola/15 dark:bg-cream/15"}`} />
      ))}
    </span>
  );
}

function ProCard({ p }) {
  const [open, setOpen] = useState(false);
  const fullName = `${p.nome || ""} ${p.cognome || ""}`.trim();

  const friendly = p.friendly_groups || [];
  const formed   = p.formed_groups || {};
  // Formed with strong signal first
  const formedList = Object.entries(formed).sort((a, b) => {
    const ord = { "sì": 0, "più sì che no": 1, "più no che sì": 2 };
    return (ord[a[1]] ?? 9) - (ord[b[1]] ?? 9);
  });

  return (
    <article
      className="rounded-2xl border border-viola/10 bg-white dark:bg-viola/60 dark:border-cream/10 p-6 transition-transform hover:-translate-y-0.5"
      data-testid={`pro-${(p.cognome || "").replace(/\s+/g, "-")}-${(p.nome || "").replace(/\s+/g, "-")}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="font-display text-2xl leading-tight dark:text-cream">{fullName}</h3>
          {p.pronomi && <p className="text-xs text-viola/60 dark:text-cream/60 mt-1">{p.pronomi}</p>}
        </div>
        {p.city && (
          <span className="inline-flex items-center gap-1 rounded-full bg-lilla px-3 py-1 text-xs font-semibold text-viola dark:bg-cream/10 dark:text-cream">
            <MapPin size={12} /> {p.city}
          </span>
        )}
      </div>

      {p.qualifica && <p className="mt-3 text-sm font-semibold text-fucsia">{p.qualifica}</p>}

      {/* Modalità + Trauma badges */}
      <div className="mt-3 flex flex-wrap gap-1">
        {p.online && <span className="text-[10px] uppercase tracking-widest bg-giallo text-viola px-2 py-1 rounded-full font-semibold">Online</span>}
        {p.in_studio && <span className="text-[10px] uppercase tracking-widest bg-arancio/70 text-viola px-2 py-1 rounded-full font-semibold">In studio</span>}
        {p.studio_accessibile && <span className="text-[10px] uppercase tracking-widest bg-arancio/70 text-viola px-2 py-1 rounded-full font-semibold" data-testid="pro-badge-accessibile">Studio accessibile</span>}
        {p.agevolate && <span className="text-[10px] uppercase tracking-widest bg-lilla text-viola px-2 py-1 rounded-full font-semibold">Tariffe agevolate</span>}
        {p.trauma && (
          <span className="text-[10px] uppercase tracking-widest bg-viola/10 text-viola px-2 py-1 rounded-full font-semibold dark:bg-cream/10 dark:text-cream" title="Formazione clinica sul trauma">
            Trauma: {p.trauma}
          </span>
        )}
      </div>

      {/* FORMED groups — formazione specialistica */}
      {formedList.length > 0 && (
        <div className="mt-4">
          <p className="text-[10px] uppercase tracking-widest font-semibold text-viola/70 dark:text-cream/70 flex items-center gap-1">
            <GraduationCap size={12} /> Formata su
          </p>
          <div className="mt-1.5 flex flex-wrap gap-1">
            {(open ? formedList : formedList.slice(0, 5)).map(([label, level]) => (
              <span key={label} className="text-[10px] rounded-full border border-fucsia/40 dark:border-fucsia/60 px-2 py-0.5 text-viola/85 dark:text-cream/85 inline-flex items-center">
                {label}
                <LevelDots level={level} />
              </span>
            ))}
            {!open && formedList.length > 5 && (
              <button onClick={() => setOpen(true)} className="text-[10px] text-fucsia font-semibold">+{formedList.length - 5}</button>
            )}
          </div>
        </div>
      )}

      {/* FRIENDLY groups — accoglienza affermativa */}
      {friendly.length > 0 && open && (
        <div className="mt-3">
          <p className="text-[10px] uppercase tracking-widest font-semibold text-viola/70 dark:text-cream/70 flex items-center gap-1">
            <Heart size={12} /> Friendly con
          </p>
          <div className="mt-1.5 flex flex-wrap gap-1">
            {friendly.map((g) => (
              <span key={g} className="text-[10px] rounded-full border border-viola/20 dark:border-cream/20 px-2 py-0.5 text-viola/70 dark:text-cream/70">{g}</span>
            ))}
          </div>
        </div>
      )}

      {open && (
        <div className="mt-4 space-y-2 text-sm text-viola/85 dark:text-cream/85 leading-relaxed">
          {p.specifiche && (
            <p><span className="font-semibold text-viola dark:text-cream">Approccio: </span>{p.specifiche}</p>
          )}
          {p.eta && (<p><span className="font-semibold">Utenza: </span>{p.eta}</p>)}
          {p.lavoro_con && (<p><span className="font-semibold">Lavoro con: </span>{p.lavoro_con}</p>)}
          {p.lingua && (<p><span className="font-semibold">Lingua: </span>{p.lingua}</p>)}
          {p.tempi && (<p><span className="font-semibold">Tempi: </span>{p.tempi}</p>)}
          {p.altro && (<p><span className="font-semibold">Altro: </span>{p.altro}</p>)}
        </div>
      )}

      {/* Contatti */}
      <div className="mt-4 flex flex-wrap gap-x-3 gap-y-1.5 text-xs">
        {p.email && (
          <a href={`mailto:${p.email}`} className="inline-flex items-center gap-1 font-semibold text-viola hover:text-fucsia dark:text-cream" title={p.email}>
            <Mail size={12} /> Email
          </a>
        )}
        {p.whatsapp && String(p.whatsapp).toLowerCase() !== "no" && (
          <span className="inline-flex items-center gap-1 text-viola/70 dark:text-cream/70">
            <MessageCircle size={12} /> WhatsApp
          </span>
        )}
        {p.telegram && String(p.telegram).toLowerCase() !== "no" && (
          <span className="inline-flex items-center gap-1 text-viola/70 dark:text-cream/70">
            <Send size={12} /> Telegram
          </span>
        )}
        {p.instagram && String(p.instagram).toLowerCase() !== "no" && !String(p.instagram).startsWith("http") && (
          <span className="inline-flex items-center gap-1 text-viola/70 dark:text-cream/70">
            <Instagram size={12} /> {p.instagram}
          </span>
        )}
        {p.sito && String(p.sito).startsWith("http") && (
          <a href={p.sito} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-semibold text-fucsia">
            <Globe size={12} /> Sito <ExternalLink size={10} />
          </a>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <button
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          className="text-xs font-semibold text-fucsia hover:underline inline-flex items-center gap-1"
          data-testid="pro-toggle-details"
        >
          {open ? (<>Nascondi dettagli <ChevronUp size={12} /></>) : (<>Vedi dettagli <ChevronDown size={12} /></>)}
        </button>
        {p.id && (
          <Link to={`/rete/scheda/${p.id}`} className="text-xs font-semibold text-viola/70 hover:text-fucsia dark:text-cream/70" data-testid="pro-page-link" aria-label={`Pagina personale di ${fullName}`}>
            Pagina personale →
          </Link>
        )}
      </div>
    </article>
  );
}

export default function Rete() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ cities: [], groups: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [q, setQ] = useState(() => searchParams.get("q") || "");
  const [filters, setFilters] = useState(() => fromParams(searchParams));
  const [showFilters, setShowFilters] = useState(false);
  const [view, setView] = useState("list");  // "list" | "map"
  const [copied, setCopied] = useState(false);

  useEffect(() => { http.get("/rete/meta").then((r) => setMeta(r.data)); }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      const params = toParams(q, filters);
      setSearchParams(params, { replace: true });
      setLoading(true);
      http.get(`/rete?${params}`).then((r) => setItems(r.data)).finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, filters, setSearchParams]);

  const copyLink = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      window.prompt("Copia questo link:", url);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const activeCount = countActive(filters);
  const clearFilters = () => { setQ(""); setFilters(EMPTY_FILTERS); };
  const hasFilters = q || activeCount > 0;
  const pickCity = (c) => {
    setFilters((f) => ({ ...f, city: f.city.includes(c) ? f.city : [...f.city, c] }));
    setView("list");
  };

  return (
    <div className="container-editorial py-16 lg:py-24 dark:text-cream" data-testid="page-rete">
      <div className="max-w-3xl">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <span className="pill">Rete GSRD</span>
          <Link to="/rete/iscriviti" className="text-sm font-semibold text-fucsia hover:underline underline-offset-4" data-testid="rete-join-link">
            Sei unə professionistə? Entra nella Rete o aggiorna la tua scheda →
          </Link>
        </div>
        <h1 className="mt-4 font-display text-5xl sm:text-6xl tracking-tighter leading-[0.95] dark:text-cream">
          Rete di professionistə<br />
          <span className="italic font-normal">Gender · Sex · Relationship Diversity.</span>
        </h1>
        <p className="mt-6 text-lg text-viola/75 dark:text-cream/75">
          Un elenco condiviso di {meta.total} professionistə della salute mentale con approccio affermativo, sex-positive e attento alla pluralità delle esperienze. Filtra per città, tema o modalità.
        </p>
        <p className="mt-3 text-sm text-viola/60 dark:text-cream/60">
          Questa rete è un lavoro collettivo. L'inclusione nel foglio è un'auto-dichiarazione dellə professionistə: verifica sempre formazione e approccio con la persona che scegli.
        </p>
        <div className="mt-5 rounded-xl bg-lilla/40 dark:bg-viola/30 p-4 text-xs text-viola/80 dark:text-cream/80 leading-relaxed">
          <p className="font-semibold uppercase tracking-widest text-viola dark:text-cream text-[10px]">Legenda</p>
          <p className="mt-1">
            <span className="inline-flex items-center gap-1 font-semibold"><Heart size={11} /> Friendly</span> = accoglie persone del gruppo senza patologizzare · &nbsp;
            <span className="inline-flex items-center gap-1 font-semibold"><GraduationCap size={11} /> Formata su</span> = ha una formazione specifica.
            &nbsp;I pallini indicano il livello di formazione dichiarato: ●●● Formazione specifica · ●● Formazione significativa · ● Formazione parziale.
          </p>
        </div>
      </div>

      {/* SEARCH BAR */}
      <div className="mt-10 flex flex-col md:flex-row gap-3 items-stretch md:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-viola/50 dark:text-cream/50" size={16} />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca per nome, città, competenza…"
            aria-label="Cerca nella rete per nome, città o competenza"
            className="w-full rounded-full border border-viola/20 bg-white dark:bg-viola/40 dark:border-cream/20 dark:text-cream pl-11 pr-5 py-3 focus:outline-none focus:ring-2 focus:ring-fucsia"
            data-testid="rete-search"
          />
        </div>
        <div className="inline-flex rounded-full border border-viola/20 dark:border-cream/20 overflow-hidden" data-testid="view-toggle">
          <button
            onClick={() => setView("list")}
            aria-pressed={view === "list"}
            className={`px-4 py-3 text-sm font-medium inline-flex items-center gap-1.5 ${view === "list" ? "bg-viola text-cream" : "text-viola dark:text-cream hover:bg-lilla/40"}`}
            data-testid="view-list"
          >
            <List size={16} /> Lista
          </button>
          <button
            onClick={() => setView("map")}
            aria-pressed={view === "map"}
            className={`px-4 py-3 text-sm font-medium inline-flex items-center gap-1.5 ${view === "map" ? "bg-viola text-cream" : "text-viola dark:text-cream hover:bg-lilla/40"}`}
            data-testid="view-map"
          >
            <MapIcon size={16} /> Mappa
          </button>
        </div>
        <button
          onClick={() => setShowFilters((v) => !v)}
          aria-expanded={showFilters}
          className={`inline-flex items-center gap-2 rounded-full px-5 py-3 font-medium transition-colors ${showFilters ? "bg-viola text-cream" : "border border-viola/20 text-viola dark:border-cream/20 dark:text-cream"}`}
          data-testid="rete-toggle-filters"
        >
          <Filter size={16} /> Filtri
          {activeCount > 0 && (
            <span className="ml-1 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-fucsia text-white text-xs font-bold" data-testid="rete-filters-count">{activeCount}</span>
          )}
        </button>
        {hasFilters && (
          <button onClick={clearFilters} className="text-sm text-fucsia font-semibold flex items-center gap-1" data-testid="rete-clear-filters">
            <X size={14} /> Reset
          </button>
        )}
      </div>

      {showFilters && (
        <ReteFiltersPanel
          meta={meta} filters={filters} setFilters={setFilters} resultCount={items.length}
          onDone={() => {
            setShowFilters(false);
            setTimeout(() => document.querySelector('[data-testid="rete-count"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
          }}
        />
      )}
      <ActiveFilters filters={filters} setFilters={setFilters} />

      <div className="mt-6 scroll-mt-24 flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-viola/60 dark:text-cream/60" data-testid="rete-count" role="status" aria-live="polite">
          {loading ? "Caricamento…" : `${items.length} professionistə trovatə`}
        </div>
        {hasFilters && (
          <button type="button" onClick={copyLink} className="inline-flex items-center gap-1.5 rounded-full border border-viola/20 dark:border-cream/25 px-3 py-1.5 text-xs font-semibold text-viola dark:text-cream hover:border-fucsia hover:text-fucsia transition-colors" data-testid="rete-copy-link">
            {copied ? <Check size={13} aria-hidden /> : <Link2 size={13} aria-hidden />}
            {copied ? "Link copiato" : "Copia link della ricerca"}
          </button>
        )}
        <span className="sr-only" aria-live="polite">{copied ? "Link della ricerca copiato" : ""}</span>
      </div>

      {view === "map" && (
        <div className="mt-6">
          <ReteMap onCitySelect={pickCity} />
        </div>
      )}

      {view === "list" && (
        <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((p, i) => <ProCard key={p.id || `${p.cognome}-${p.nome}-${p.city}-${i}`} p={p} />)}
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="mt-10 rounded-2xl bg-lilla/40 dark:bg-viola/30 p-12 text-center" data-testid="rete-empty">
          <p className="font-display text-2xl dark:text-cream">Nessunə professionistə trovatə con questi filtri.</p>
          <p className="mt-3 text-sm text-viola/70 dark:text-cream/70">Prova a togliere qualche filtro o a scegliere più città.</p>
          {hasFilters && (
            <button onClick={clearFilters} className="mt-5 rounded-full bg-viola text-cream px-5 py-2.5 text-sm font-semibold" data-testid="rete-empty-reset">Azzera i filtri</button>
          )}
        </div>
      )}
    </div>
  );
}
